import { toast } from "react-toastify";
export const SoundType = {
  QUOTE: 1,
  ORDER: 2,
};

export const UserType = {
  Advisor: "Advisor",
  Trader: "Trader",
  MiddleOffice: "MiddleOffice",
  BackOffice: "BackOffice",
  FundManager: "FundManager",
  Accounting: "Accounting",
};

export const isHeadAdvisor = (roleNames) =>
  Array.isArray(roleNames) && roleNames.some((role) => String(role).trim().toUpperCase() === "HEAD");

export const playSound = (type, soundPath = "sounds/") => {
  let _soundPath = soundPath;

  switch (type) {
    case SoundType.QUOTE:
      _soundPath += "sound_quote.mp3";
      break;

    case SoundType.ORDER:
      _soundPath += "sound_order.mp3";
      break;
  }

  const audio = new Audio(_soundPath);

  audio.play();

  // delete audio;
};

export const scale = ({ domain, range }) => {
  const istart = domain[0];
  const istop = domain[1];
  const ostart = range[0];
  const ostop = range[1];

  return (value) => ostart + (ostop - ostart) * ((value - istart) / (istop - istart));
};

let serverTimezoneOffset = null;

function setAndGetServerTimezoneOffset() {
  const context = JSON.parse(localStorage.getItem("context"));

  if (context && context.timezoneOffset) {
    serverTimezoneOffset = context.timezoneOffset;
  } else {
    serverTimezoneOffset = 0;
  }

  return serverTimezoneOffset;
}

export const localDateTime = (dateTime) => {
  if (!dateTime || typeof dateTime !== "string" || dateTime.substring(0, 10) == "0000-00-00") {
    return dateTime;
  }

  const _timeZoneOffset =
    serverTimezoneOffset != null ? serverTimezoneOffset : setAndGetServerTimezoneOffset();

  // valid inputs
  // 	 2018-06-14T19:35:3
  //   19:35:3

  // Server time come as 2018-06-14T19:35:3 with offset in minutes = timeZoneOffset
  const date = new Date(`${dateTime}Z`);

  return new Date(date.getTime() + _timeZoneOffset * 60 * 1000);
};

export const showDateTime = (dateTime) => {
  if (!dateTime || typeof dateTime !== "string" || dateTime.substring(0, 10) === "0000-00-00") {
    return dateTime;
  }

  // this receives a date string without offset and returns time string
  // to show full date time use:
  //   <FormattedDate value={localDateTime(datetime)} />
  //   {showDateTime(dateTime)} // not <ForamttedTime value={localDateTime(dateTime)}

  return localDateTime(dateTime).toTimeString().substr(0, 8);
};

export const localDateToGMT = (dateTime) => {
  if (!dateTime || dateTime.substring(0, 10) == "0000-00-00") return dateTime;

  const _timeZoneOffset =
    serverTimezoneOffset != null ? serverTimezoneOffset : setAndGetServerTimezoneOffset();

  // valid inputs
  // 	 2018-06-14T19:35:3
  //   19:35:3

  // Server time come as 2018-06-14T19:35:3 with offset in minutes = timeZoneOffset
  const date = new Date(dateTime);

  return new Date(date.getTime() + _timeZoneOffset * 60 * 1000);
};

export function toDateLocal(date) {
  const ten = function (i) {
    return (i < 10 ? "0" : "") + i;
  };
  const YYYY = date.getFullYear();
  const MM = ten(date.getMonth() + 1);
  const DD = ten(date.getDate());
  return `${YYYY}-${MM}-${DD}`;
}

export function debounce(fn, delay) {
  let timer = null;
  return function () {
    const context = this;
    const args = arguments;
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(context, args);
    }, delay);
  };
}

/**
 * Performs a deep merge of `source` into `target`.
 * Mutates `target` only but not its objects and arrays.
 *
 * @author inspired by [jhildenbiddle](https://stackoverflow.com/a/48218209).
 */
export function mergeDeep(target, source) {
  const isObject = (obj) => obj && typeof obj === "object";

  if (!isObject(target) || !isObject(source)) {
    return source;
  }

  Object.keys(source).forEach((key) => {
    const targetValue = target[key];
    const sourceValue = source[key];

    if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
      target[key] = sourceValue;
    } else if (isObject(targetValue) && isObject(sourceValue)) {
      target[key] = mergeDeep({ ...targetValue }, sourceValue);
    } else {
      target[key] = sourceValue;
    }
  });

  return target;
}

/**
 * Intercept fetchs and fire pre and post events
 *
 * events = {
 * 	request?(url: string, config: any): Promise<any[]> | any[];
 *  requestError?(error: any): Promise<any>;
 *  response?(response: Response): Response;
 *  responseError?(error: any): Promise<any>;
 * }
 *
 * @author  https://github.com/werk85/fetch-intercept.
 */
const interceptors = [];

function interceptor(fetch, ...args) {
  const reversedInterceptors = interceptors.reduce((array, interceptor) => [interceptor].concat(array), []);
  let promise = Promise.resolve(args);

  // Register request interceptors
  reversedInterceptors.forEach(({ request, requestError }) => {
    if (request || requestError) {
      promise = promise.then((args) => request(...args), requestError);
    }
  });

  // Register fetch call
  promise = promise.then((args) => fetch(...args));

  // Register response interceptors, add request object to response
  reversedInterceptors.forEach(({ response, responseError }) => {
    if (response || responseError) {
      promise = promise.then((res) => response(res, args), responseError);
    }
  });

  return promise;
}

export function fetchIntercept(events) {
  if (!window.fetch.hasOwnProperty("__attached__")) {
    const nativeFetch = window.fetch;
    window.fetch = (...args) => interceptor(nativeFetch, ...args);
    window.fetch.__attached__ = true;
  }

  interceptors.push(events);

  return () => {
    const index = interceptors.indexOf(events);
    if (index >= 0) {
      interceptors.splice(index, 1);
    }
  };
}

export const handleFileInput = (input) =>
  new Promise((resolve, reject) => {
    const { file } = input;
    const preName = file.name || "attachment.txt";
    const name = preName.substring(0, preName.lastIndexOf("."));
    const extension = preName.substring(preName.lastIndexOf(".") + 1, preName.length);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const items = reader.result.split(",");
      resolve({ blob: items[1], name, extension });
    };
    reader.onerror = (error) => {
      reject(error);
    };
  });

export function valueIsEmpty(value) {
  return value === undefined || value === null || value === "";
}

export function dateIsEmpty(value) {
  return new Date(value) == "Invalid Date" || isNaN(new Date(value));
}

export const processes = {
  ORDER: "Order",
  OFFER: "Offer",
  TRADE: "Trade",
  BULK_ORDER: "BulkOrder",
  BASKET_ORDER: "BasketOrder",
  WITHDRAW: "Withdraw",
  BULK_WITHDRAW: "BulkWithdraw",
  DEPOSIT: "Deposit",
  BULK_DEPOSIT: "BulkDeposit",
  TRANSFER: "Transfer",
  TRANSACTION: "Transaction",
  CORP_EVENT: "CorpEvent",
  CASH_ACTIVITY: "CashActivity",
  PRODUCT_ACTIVITY: "ProductActivity",
  JOURNAL_ENTRY: "JournalEntry",
  INVOICE: "Invoice",
  SETTLEMENT_INSTRUCTION: "SettleInst",
  SHORT_COVER: "ShortCover",
  EARNING_RECOVERY: "EarningRecovery",
  PROVISIONS_PAYMENT: "ProvisionsPayment",
  WITHDRAW_ACCOUNT: "WithdrawAccount",
  DEPOSIT_ACCOUNT: "DepositAccount",
  RECURRINGCOMMISSIONS: "RecurringCommissions",
  RECCOMMISSIONACCOUNT: "RecCommissionAccount",
  CUSTOM_EVENT: "CustomEvent",
  CUSTODY_LOCK: "CustodyLock",
  SUPPLIER_INVOICE: "SupplierInvoice",
  INVOICE_PAYMENT: "InvoicePayment",
  AMORTIZATION: "Amortization",
  DERIVATIVE_MATURITY: "DerivativeMaturity",
  INVOICE_REQUEST: "InvoiceRequest",
};

toast.configure({
  newestOnTop: true,
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
});

export const showToast = (text, err = false, warn = false) => {
  if (err) {
    return toast.error(text);
  }
  if (warn) {
    return toast.warning(text);
  }
  return toast.info(text);
};

export const PersonMode = {
  New: "New",
  Edit: "Edit",
};

export const AccountMode = {
  New: "New",
  Edit: "Edit",
};

export const societyTypes = [
  { id: "NGO", name: "ONG" },
  { id: "Commercial", name: "Comercial" },
  { id: "Investment", name: "Vehiculo de Inversion" },
];

export const PersonQualification = [
  { id: "Qualified", name: "Qualified" },
  { id: "Unqualified", name: "Unqualified" },
];

export const AccountFoundsOrigin = [
  { id: "Own", name: "Own" },
  { id: "ThirdParties", name: "Third Parties" },
  { id: "Other", name: "Other" },
];

export const AccountPurpose = [
  { id: "Rent", name: "Rent" },
  { id: "CapitalGrowth", name: "Capital Growth" },
  { id: "CapitalPreservation", name: "Capital Preservation" },
  { id: "Speculation", name: "Speculation" },
  { id: "Other", name: "Other" },
  { id: "", name: "" },
];

export const CustodianEntityStatus = [
  { id: "Active", name: "Active" },
  { id: "Inactive", name: "Inactive" },
];

export const CounterPartyStatus = [
  { id: "Active", name: "Active" },
  { id: "Inactive", name: "Inactive" },
  { id: "InProcess", name: "InProcess" },
];

export const AccountBookInfo = {
  LOCAL_BOOKING: "LocalBooking",
  THIRD_PARTY_BOOKING: "ThirdPartyBooking",
  BOTH: "Both",
};

export const BookTypes = {
  SELF: "Self",
  PERSHING: "Pershing",
  IB: "IB",
  FC_STONE: "FCStone",
  ALL: "All",
};

export const LegalAddresses = {
  Personal: "Personal",
  Work: "Work",
  "": "",
};

export const getDefaultBookType = (AccountBooktypes) => {
  if (!AccountBooktypes || AccountBooktypes.some((i) => i == BookTypes.SELF)) return BookTypes.SELF;

  return AccountBooktypes[0];
};

export const Commissions = {
  DETAILED: { value: "detailed", description: "Detailed" },
  CONDENSED: { value: "condensed", description: "Condensed" },
};

export const emailStatus = {
  1: "Scheduled",
  2: "Processing",
  3: "Sent",
  4: "With Errors",
  5: "Canceled",
};

export const getColorSchReportStatus = (status) => {
  if (status == 1) return "color1";
  if (status == 2) return "color2";
  if (status == 3) return "color4";
  if (status == 4) return "color3";
  if (status == 5) return "color5";
};

export const parseScheduleReportXML = (s) => {
  var parser = new DOMParser();
  var xmlDoc = parser.parseFromString(s.Body, "text/xml");
  return {
    OTCFechaConcertacion: xmlDoc.getElementsByTagName("OTCFechaConcertacion")[0].childNodes[0].nodeValue,
    OTCOperacionNumero: xmlDoc.getElementsByTagName("OTCOperacionNumero")[0].childNodes[0].nodeValue,
    OTCTipoOperacion: xmlDoc.getElementsByTagName("OTCTipoOperacion")[0].childNodes[0].nodeValue,
    OTCCompradorCodigo: xmlDoc.getElementsByTagName("OTCCompradorCodigo")[0].childNodes[0].nodeValue,
    OTCVendedorCodigo: xmlDoc.getElementsByTagName("OTCVendedorCodigo")[0].childNodes[0].nodeValue,
    OTCISIN: xmlDoc.getElementsByTagName("OTCISIN")[0].childNodes[0].nodeValue,
    OTCPrecioCC: xmlDoc.getElementsByTagName("OTCPrecioCC")[0].childNodes[0].nodeValue,
    OTCPrecioSC: xmlDoc.getElementsByTagName("OTCPrecioSC")[0].childNodes[0].nodeValue,
    OTCTasa: xmlDoc.getElementsByTagName("OTCTasa")[0].childNodes[0].nodeValue,
    OTCValorNominal: xmlDoc.getElementsByTagName("OTCValorNominal")[0].childNodes[0].nodeValue,
    OTCEfectivo: xmlDoc.getElementsByTagName("OTCEfectivo")[0].childNodes[0].nodeValue,
    OTCEfectivoUSD: xmlDoc.getElementsByTagName("OTCEfectivoUSD")[0].childNodes[0].nodeValue,
    OTCFechaLiquidacion: xmlDoc.getElementsByTagName("OTCFechaLiquidacion")[0].childNodes[0].nodeValue,
    OTCLiquidaBVM: xmlDoc.getElementsByTagName("OTCLiquidaBVM")[0].childNodes[0].nodeValue,
    OTCMonedaBCU: xmlDoc.getElementsByTagName("OTCMonedaBCU")[0].childNodes[0].nodeValue,
  };
};
export const instructionWrittenTypes = [
  { id: "Otros medios electrónicos", description: "Otros medios electrónicos" },
  { id: "Correo electrónico", description: "Correo electrónico" },
  { id: "Escrita", description: "Escrita" },
];

export const instructionWebTypes = [
  { id: "ClientX", description: "ClientX" },
  { id: "APP", description: "APP" },
];
